/**
 * Renders a completed review's deliverable (the §9 fields). Includes the
 * disclaimer that this is independent verification, not a grade change.
 *
 * @module ReviewResultPanel
 */
import { useTranslation } from 'react-i18next';

import type { ReviewResult } from '@/lib/humanReviewsApi';

export function ReviewResultPanel({ result }: { readonly result: ReviewResult }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
      <p className="font-ui text-xs font-bold uppercase tracking-widest text-emerald-700">
        {t('humanReview.result.title')}
      </p>

      <div className="space-y-1">
        <p className="font-ui text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {t('humanReview.result.verifiedBand')}
        </p>
        <p className="font-ui text-sm text-foreground">{result.verifiedBand}</p>
      </div>

      <div className="space-y-1">
        <p className="font-ui text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {t('humanReview.result.readinessVerdict')}
        </p>
        <p className="font-ui text-sm leading-relaxed text-foreground whitespace-pre-wrap">
          {result.readinessVerdict}
        </p>
      </div>

      {result.prioritizedFixes.length > 0 && (
        <div className="space-y-1">
          <p className="font-ui text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('humanReview.result.prioritizedFixes')}
          </p>
          <ol className="font-ui list-decimal space-y-1 pl-5 text-sm text-foreground">
            {result.prioritizedFixes.map((fix, idx) => (
              <li key={idx}>{fix}</li>
            ))}
          </ol>
        </div>
      )}

      <div className="space-y-1">
        <p className="font-ui text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {t('humanReview.result.attachedQuestionAnswer')}
        </p>
        <p className="font-ui text-sm leading-relaxed text-foreground whitespace-pre-wrap">
          {result.attachedQuestionAnswer}
        </p>
      </div>

      <p className="font-ui border-t border-emerald-200/70 pt-2 text-[11px] italic text-muted-foreground">
        {t('humanReview.result.disclaimer')}
      </p>
    </div>
  );
}
